/**
 * AcuityClientSyncModal - selective client import from Acuity.
 * Shelsea ticks which Acuity clients to onboard. The rest stay in Acuity-only.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Search, AlertCircle, Users, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcuityClientSyncModal({ open, onClose, eventId, onImported }) {
  const [clients, setClients] = useState([]);
  const [connected, setConnected] = useState(false);
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const auth = useCallback(() => {
    const t = localStorage.getItem("token") || localStorage.getItem("session_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/acuity/sync-clients-preview`,
        { event_id: eventId },
        { headers: auth(), withCredentials: true });
      setClients(res.data?.acuity_clients || []);
      setConnected(res.data?.acuity_connected || false);
      setSelected({});
    } catch (e) {
      toast.error("Could not load Acuity clients");
    } finally {
      setLoading(false);
    }
  }, [eventId, auth]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggleAll = (checked) => {
    const next = {};
    if (checked) {
      filtered.forEach((c) => { if (!c.already_imported) next[c.id || c.email] = true; });
    }
    setSelected(next);
  };

  const importSelected = async () => {
    const selectedClients = clients.filter((c) => selected[c.id || c.email] && !c.already_imported);
    if (selectedClients.length === 0) { toast.error("Pick at least one client"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/acuity/sync-clients-import`,
        { event_id: eventId, selected_clients: selectedClients },
        { headers: auth(), withCredentials: true });
      toast.success(`${res.data.imported} client${res.data.imported === 1 ? "" : "s"} imported`);
      if (onImported) onImported();
      onClose();
    } catch (e) {
      toast.error("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const filtered = clients.filter((c) => {
    if (!search) return true;
    const blob = `${c.firstName || ""} ${c.lastName || ""} ${c.email || ""}`.toLowerCase();
    return blob.includes(search.toLowerCase());
  });

  const selectableCount = filtered.filter((c) => !c.already_imported).length;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Pick which Acuity clients to onboard onto Phygital</DialogTitle>
          <p className="text-xs text-gray-500 mt-1">
            Tick the clients you want to add. The rest stay in your Acuity account only. You can always re-run this later.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-2">
          {!connected ? (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Acuity is not connected</p>
                  <p className="text-xs text-gray-600 mt-1">Connect Acuity in Settings → Tech Stack first.</p>
                </div>
              </CardContent>
            </Card>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 sticky top-0 bg-white py-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name or email…"
                    className="pl-7 h-8 text-xs"
                    data-testid="client-sync-search"
                  />
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => toggleAll(selectedCount < selectableCount)}
                  data-testid="client-sync-toggle-all"
                >
                  {selectedCount < selectableCount ? "Select all" : "Clear all"}
                </Button>
                <span className="text-xs text-gray-500">{selectedCount} of {selectableCount}</span>
              </div>

              {filtered.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">
                  {clients.length === 0 ? "No clients in your Acuity account yet." : "No matches for that search."}
                </p>
              ) : filtered.map((c) => {
                const id = c.id || c.email;
                return (
                  <Card key={id} className={`${c.already_imported ? "opacity-50" : "hover:bg-gray-50"}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <Checkbox
                        checked={!!selected[id]}
                        disabled={c.already_imported}
                        onCheckedChange={(v) => setSelected((s) => ({ ...s, [id]: v }))}
                        data-testid={`client-sync-checkbox-${id}`}
                      />
                      <Users className="w-4 h-4 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{c.firstName} {c.lastName}</p>
                        <p className="text-[10px] text-gray-500 flex items-center gap-2">
                          {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                          {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                        </p>
                      </div>
                      {c.already_imported && <Badge variant="outline" className="text-[9px]">Already imported</Badge>}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={importSelected} disabled={!connected || saving || selectedCount === 0} data-testid="client-sync-import-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Import {selectedCount} client{selectedCount === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
